/**
 * Gift milestones — auto-add and auto-remove free gifts.
 *
 * Config (thresholds, gift variant ids) is server-rendered into
 * data-gift-milestones by free-gift-notice.liquid. Live cart state is
 * always fetched fresh from /cart.js, so reconciliation never acts on a
 * stale snapshot and doesn't depend on any particular drawer re-render.
 *
 * Triggers: the theme's own bubbling cart events (on:cart:add,
 * on:line-item:change), plus a body-level observer as a fallback for any
 * cart re-render path that doesn't dispatch them, plus one run on load.
 *
 * Behaviour:
 *   - earned milestone, gift not in cart → add it (marked _auto_gift)
 *   - threshold no longer met, gift was auto-added → remove it
 *   - customer deliberately removed an auto-added gift → leave it out for
 *     the rest of the session (re-add once policy)
 *
 * Pricing is enforced by the discount function server-side; this layer is
 * pure UX. If an add fails (e.g. gift out of stock) it is retried on the
 * next cart change.
 */
(() => {
  const DISMISSED_KEY = 'dashcartDismissedGifts';
  const DEAD_KEY = 'dashcartDeadGiftPrograms';
  const STRIKE_KEY = 'dashcartGiftProgramStrikes';
  const ADD_COUNT_KEY = 'dashcartGiftAddCounts';
  let reconciling = false;
  let timer = null;

  const readSet = (key) => {
    try { return new Set(JSON.parse(sessionStorage.getItem(key) || '[]')); }
    catch { return new Set(); }
  };
  const writeSet = (key, set) => sessionStorage.setItem(key, JSON.stringify([...set]));
  const readMap = (key) => {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}') || {}; }
    catch { return {}; }
  };
  const writeMap = (key, map) => sessionStorage.setItem(key, JSON.stringify(map));

  const getPrograms = () => {
    // The attribute lives on the notice itself (always rendered when any
    // gift program exists — even with an empty cart, so threshold-drop slate
    // wipes still run).
    const holder = document.querySelector('#free-gift-notice[data-gift-programs]');
    if (!holder) return null;
    try {
      const parsed = JSON.parse(holder.dataset.giftPrograms);
      return Array.isArray(parsed) ? parsed : null;
    } catch { return null; }
  };

  const hideProgramBar = (key) => {
    document.querySelectorAll(`[data-gift-program="${key}"]`).forEach((el) => { el.hidden = true; });
  };

  const refreshCart = async () => {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refreshCartItems === 'function' && cartItems.dataset.empty === 'false') {
      await cartItems.refreshCartItems();
    } else {
      window.location.reload();
    }
  };

  const reconcile = async () => {
    if (reconciling) return;
    const programs = getPrograms();
    if (!programs || programs.length === 0) return;

    reconciling = true;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      // A gift never counts toward any program's spend, even another's.
      const giftIds = new Set(programs.flatMap((p) => p.milestones.map((m) => Number(m.variantId))));

      const dismissed = readSet(DISMISSED_KEY);
      const addCounts = readMap(ADD_COUNT_KEY);
      const actions = [];

      const dead = readSet(DEAD_KEY);
      const strikes = readMap(STRIKE_KEY);
      for (const key of dead) hideProgramBar(key);

      for (const program of programs) {
      if (dead.has(String(program.key))) continue;
      // Dead-promo guard: an auto-added gift that ISN'T free means the
      // backing discount no longer exists (deleted promos have no hook).
      // Two consecutive sightings → remove the gift and hide this bar for
      // the session. Customers can never be charged for a "free" gift.
      let strike = false;
      for (const m of program.milestones) {
        const gLine = cart.items.find((item) => item.variant_id === Number(m.variantId) && item.properties && item.properties._auto_gift);
        if (gLine && gLine.final_line_price > 0) strike = true;
      }
      if (strike) {
        const count = (strikes[String(program.key)] || 0) + 1;
        strikes[String(program.key)] = count;
        writeMap(STRIKE_KEY, strikes);
        if (count >= 2) {
          dead.add(String(program.key));
          writeSet(DEAD_KEY, dead);
          hideProgramBar(String(program.key));
          for (const m of program.milestones) {
            const gLine = cart.items.find((item) => item.variant_id === Number(m.variantId) && item.properties && item.properties._auto_gift);
            if (gLine) actions.push({ type: 'remove', key: gLine.key });
          }
          continue;
        }
      } else if (strikes[String(program.key)]) {
        delete strikes[String(program.key)];
        writeMap(STRIKE_KEY, strikes);
      }
      const scopeIds = Array.isArray(program.scopeProductIds) ? program.scopeProductIds : [];
      let spend = 0;
      for (const item of cart.items) {
        if (giftIds.has(item.variant_id)) continue;
        if (scopeIds.length > 0 && !scopeIds.includes(item.product_id)) continue;
        spend += program.basis === 'post-discount' ? item.final_line_price : item.original_line_price;
      }

      for (const m of program.milestones) {
        const id = Number(m.variantId);
        const key = String(id);
        const earned = spend >= m.thresholdCents;
        const line = cart.items.find((item) => item.variant_id === id);
        const autoLine = line && line.properties && line.properties._auto_gift ? line : null;

        if (!earned) {
          // Below the threshold: remove an auto-added gift and wipe this
          // milestone's slate — re-crossing the threshold starts fresh, so
          // a long shopping session can never go permanently silent.
          dismissed.delete(key);
          delete addCounts[key];
          if (autoLine) actions.push({ type: 'remove', key: autoLine.key });
          continue;
        }

        if (line) {
          // Self-heal reconciler artifacts: collapse duplicate auto-added
          // lines and clamp the auto line to qty 1 (customer-added lines
          // are never touched).
          const autoLines = cart.items.filter((item) => item.variant_id === id && item.properties && item.properties._auto_gift);
          for (const extra of autoLines.slice(1)) actions.push({ type: 'remove', key: extra.key });
          if (autoLines[0] && autoLines[0].quantity > 1) actions.push({ type: 'clamp', key: autoLines[0].key });
          continue; // earned and present: healthy
        }

        // Earned but missing. Re-add once after a customer deletion, then
        // respect their choice while the cart stays qualified.
        if (dismissed.has(key)) continue;
        const count = addCounts[key] || 0;
        if (count >= 2) {
          dismissed.add(key);
          continue;
        }
        actions.push({ type: 'add', id });
      }
      }

      writeSet(DISMISSED_KEY, dismissed);
      writeMap(ADD_COUNT_KEY, addCounts);
      if (actions.length === 0) return;

      for (const action of actions) {
        if (action.type === 'add') {
          // Just-in-time check: another reconcile cycle (or the customer)
          // may have added the gift while this run was in flight.
          const fresh = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
          if (fresh.items.some((item) => item.variant_id === action.id)) continue;
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: action.id, quantity: 1, properties: { _auto_gift: '1' } })
          });
          if (res.ok) {
            const counts = readMap(ADD_COUNT_KEY);
            counts[String(action.id)] = (counts[String(action.id)] || 0) + 1;
            writeMap(ADD_COUNT_KEY, counts);
          }
        } else {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: action.key, quantity: action.type === 'clamp' ? 1 : 0 })
          });
        }
      }
      await refreshCart();
    } catch (error) {
      console.error('[gift-milestones]', error); // eslint-disable-line
    } finally {
      reconciling = false;
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 600);
  };

  // Primary triggers: the theme's own cart events (bubbling).
  document.addEventListener('on:cart:add', schedule);
  document.addEventListener('on:line-item:change', schedule);

  // Fallback: any re-render that swaps the gift notice back into the DOM.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Re-renders may replace the notice, insert content INSIDE it, or swap
      // an ancestor — treat any mutation touching it as a trigger.
      const target = mutation.target;
      if (target.nodeType === 1 && (target.id === 'free-gift-notice' || (target.closest && target.closest('#free-gift-notice')))) {
        schedule();
        return;
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.id === 'free-gift-notice'
            || (node.closest && node.closest('#free-gift-notice'))
            || (node.querySelector && node.querySelector('#free-gift-notice'))) {
          schedule();
          return;
        }
      }
    }
  });

  const init = () => {
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
  };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
